import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import type { DemoSummary } from "../types";
import { Shell, StatusPill, when } from "../ui";

/* A table from `lg` up and a stack of cards below it. Not `md`, as the calls table on the
 * demo page uses: these fixed tracks need about 940px, which is `lg` with the page gutters
 * taken off. Below that the row says the same facts in two short lines. */
const COLS = "lg:grid-cols-[minmax(0,300px)_150px_130px_90px_150px_1fr]";

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col gap-1 px-4 sm:px-5 py-3.5 sm:py-4 bg-paper border border-line rounded-[10px] sm:w-[200px]">
      <div className="text-[12px] text-muted">{label}</div>
      <div className="text-[22px] sm:text-[26px] font-semibold">{value}</div>
    </div>
  );
}

export function DemosPage() {
  const [demos, setDemos] = useState<DemoSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<{ demos: DemoSummary[] }>("/v1/demos")
      .then((body) => setDemos(body.demos))
      .catch((err: ApiError) => setError(err.message));
  }, []);

  useEffect(load, [load]);

  /* A draft finishes on the server while this list is open, so the row that says "Drafting…"
   * would otherwise sit there saying it until someone reloaded. Only while one is running:
   * a list of settled demos has nothing to poll for. */
  const drafting = demos?.some((d) => d.status === "drafting") ?? false;
  useEffect(() => {
    if (!drafting) return;
    const id = setInterval(load, 15_000);
    return () => clearInterval(id);
  }, [drafting, load]);

  const monthAgo = Date.now() - 30 * 86_400_000;
  const live = demos?.filter((d) => d.status === "live").length ?? 0;
  const calls = demos?.reduce((n, d) => n + (d.last_call_at && d.last_call_at > monthAgo ? d.calls : 0), 0) ?? 0;
  const promoted = demos?.filter((d) => d.status === "promoted").length ?? 0;
  const toCheck = demos?.filter((d) => d.status === "review" && d.pending_count > 0).length ?? 0;

  return (
    <Shell>
      <main className="flex flex-col gap-6 px-4 sm:px-6 md:px-10 py-6 sm:py-8 grow">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="m-0 text-[26px] font-semibold">Demos</h1>
            <div className="text-[14px] text-muted">One link per prospect. A demo expires after 90 days unless you extend it.</div>
          </div>
          <Link to="/new" className="inline-flex items-center gap-2 h-11 px-[18px] bg-ink text-white rounded-lg text-[14px] font-medium hover:text-white hover:bg-ink-soft">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            New demo
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
          <Stat label="Live now" value={live} />
          <Stat label="Demo calls this month" value={calls} />
          <Stat label="Needs your review" value={toCheck} />
          <Stat label="Handed to the build team" value={promoted} />
        </div>

        {error && <div className="text-[14px] text-warn">{error}</div>}

        <div className="flex flex-col bg-paper border border-line rounded-card overflow-x-auto">
          <div className={`hidden lg:grid ${COLS} gap-4 px-5 py-3 text-[12px] text-muted border-b border-line bg-paper-soft min-w-[1100px]`}>
            <div>Business</div><div>Status</div><div>Visits</div><div>Calls</div><div>Last call</div><div>Link</div>
          </div>
          {demos === null && !error && <div className="px-5 py-6 text-[14px] text-muted">Loading…</div>}
          {demos?.length === 0 && <div className="px-5 py-6 text-[14px] text-muted">No demos yet. Start one from a prospect's website.</div>}
          {demos?.map((d) => {
            const to = d.status === "live" || d.status === "paused" || d.status === "expired" || d.status === "promoted"
              ? `/demos/${d.id}` : d.status === "draft" && !d.draft_error && d.pending_count === 0 && !d.draft_run_id
                ? `/demos/${d.id}/review` : `/demos/${d.id}/review`;
            // A published demo is one that has been live - the passcode says nothing about
            // that any more, now that a demo link needs no code.
            const published = d.status === "live" || d.status === "paused"
              || d.status === "expired" || d.status === "promoted";
            return (
              <Link key={d.id} to={to} className={`flex flex-col gap-1.5 lg:grid ${COLS} lg:gap-4 lg:items-center px-4 sm:px-5 py-4 border-b border-line-soft last:border-b-0 text-[14px] lg:min-w-[1100px]`}>
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-medium truncate">{d.business_name || d.website_url}</span>
                    <span className="text-[12px] text-muted truncate">{[d.kind, d.website_url.replace(/^https?:\/\//, "")].filter(Boolean).join(" · ")}</span>
                  </div>
                  <span className="lg:hidden shrink-0"><StatusPill status={d.status} pending={d.pending_count} expiresAt={d.expires_at} /></span>
                </div>
                {/* What the four hidden columns say, so a phone loses the table and not the facts. */}
                <div className="lg:hidden flex flex-col gap-0.5 text-[12px] text-muted min-w-0">
                  <span className="truncate">
                    {d.visits || "No"} visit{d.visits === 1 ? "" : "s"} · {d.calls || "no"} call{d.calls === 1 ? "" : "s"}
                    {d.last_call_at ? ` · last call ${when(d.last_call_at).toLowerCase()}` : ""}
                  </span>
                  <span className={`font-mono truncate ${published ? "" : "text-faint"}`}>{published ? `/call/${d.slug}` : "not published"}</span>
                </div>
                <div className="hidden lg:block"><StatusPill status={d.status} pending={d.pending_count} expiresAt={d.expires_at} /></div>
                <div className="hidden lg:flex flex-col gap-0.5">
                  <span className={d.visits ? "" : "text-muted"}>{d.visits || "–"}</span>
                  {d.visits > 0 && <span className="text-[12px] text-muted">{when(d.last_visit_at)}</span>}
                </div>
                <div className={`hidden lg:block ${d.calls ? "" : "text-muted"}`}>{d.calls || "–"}</div>
                <div className="hidden lg:block text-muted">{when(d.last_call_at)}</div>
                <div className={`hidden lg:block font-mono text-[13px] ${published ? "text-muted" : "text-faint"}`}>
                  {published ? `/call/${d.slug}` : "not published"}
                </div>
              </Link>
            );
          })}
        </div>
      </main>
    </Shell>
  );
}
